import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { filter } from 'rxjs';

import { Store } from '@ngrx/store';

import {
  selectChatState,
  selectSelectedChannel,
  selectSelectedWorkSpace,
  selectUiState,
  selectUser,
} from '../../../store/app.selectors';

import { MessagingEnvironmentService } from '../../../core/services/messaging-environment.service';
import { findOwnerUser, getTagValue } from '../../../core/utils/owner-match';
import { IUserProfile } from '../../user/interfaces/IUserProfile';
import { IAttachment } from '../interfaces/IAttachment';
import { IChannel, IChannelFilter } from '../interfaces/IChannel';
import { DeliveryStatusType, DeliveryStatusTypesOptions, IMessage, ISocketMessage } from '../interfaces/IMessage';
import { IMessageableEntity } from '../interfaces/IMessageableEntity';
import { IWorkspace, WorkspaceFeatures } from '../interfaces/IWorkspace';
import {
  changeMessageDeliveryStatus,
  changeReadChannelStatus,
  getMessages,
  setLoadingOldMessages,
  setMessageDeliveryStatus,
  setUnreadChannelCount,
} from '../store/chat.actions';
import { AppStateWithChat } from '../store/chat.reducer';
import { AttachmentsService } from './attachments.service';
import { PermissionsService } from './permissions.service';

@Injectable()
export class MessagesService {
  profileInfo: IUserProfile | null = null;
  user$ = this.store.select(selectUser);
  selectedChannel$ = this.store.select(selectSelectedChannel);
  selectedWorkspace$ = this.store.select(selectSelectedWorkSpace);
  selectedChannel: IChannel | null = null;
  showScrollButton: boolean;
  selectedWorkspace: IWorkspace | null = null;
  scrollPosition: number;
  channelsFilters: IChannelFilter;
  users: IMessageableEntity[];

  constructor(
    private titleService: Title,
    private store: Store<AppStateWithChat>,
    private permissionsService: PermissionsService,
    private attachmentsService: AttachmentsService,
    private environmentService: MessagingEnvironmentService
  ) {
    this.getChatSubscriptions();
    this.getUserSubscriptions();
    this.getUiSubscriptions();
    this.getWorkspacesSubscription();
  }

  getUserSubscriptions(): void {
    this.user$.subscribe(async (user) => {
      this.profileInfo = user;
    });
  }

  getWorkspacesSubscription(): void {
    this.selectedWorkspace$.pipe(filter((data) => data !== undefined)).subscribe((workspace) => {
      this.selectedWorkspace = workspace;
    });
  }

  getChatSubscriptions() {
    this.store
      .select(selectChatState)
      .pipe(filter((data) => data !== undefined))
      .subscribe(({ channelsFilter, unreadMessages, users, selectedChannel }) => {
        this.setUnreadMessages(unreadMessages);
        this.channelsFilters = channelsFilter;
        this.users = users;
        this.selectedChannel = selectedChannel;
      });
  }

  getUiSubscriptions() {
    this.store.select(selectUiState).subscribe(({ showScrollButton, scrollPosition }) => {
      this.showScrollButton = showScrollButton;
      this.scrollPosition = scrollPosition;
    });
  }

  getLastMessage(messages: IMessage[]): IMessage | null {
    return messages.length ? { ...messages[messages.length - 1] } : null;
  }

  sortMessages(messages: IMessage[]): IMessage[] {
    if (!messages || !messages.length) {
      return [];
    }
    const sortMessages = [...messages].sort((message1, message2) => {
      return new Date(message1.createdAt).getTime() - new Date(message2.createdAt).getTime();
    });
    return sortMessages;
  }

  deleteDuplicates(messages: IMessage[]): void {
    const aux: {
      [key: string]: boolean;
    } = {};
    for (const message of messages) {
      const systemTagUid = message.systemTags?.find((e) => e.includes('messageUID:'));
      if (aux[message.uid] || (systemTagUid && aux[systemTagUid])) {
        message.isDeleted = true;
      }
      if (message.uid) {
        aux[message.uid] = true;
      }
      if (systemTagUid) {
        aux[systemTagUid] = true;
      }
    }
  }

  setUnreadMessages(count: number) {
    if (this.environmentService.env.canChangePageTitle) {
      if (!count) {
        this.titleService.setTitle('MessagingUI');
      } else {
        this.titleService.setTitle(`MessagingUI (${count})`);
      }
    }
  }

  setMesageDeliveryStatus(bodyJson: ISocketMessage, value: DeliveryStatusType): void {
    this.store.dispatch(
      setMessageDeliveryStatus({
        messageUID: bodyJson.message.uid,
        status: value,
        channelUID: bodyJson.channel.uid,
      })
    );
  }

  clearMessage(message: IMessage) {
    if (!this.selectedChannel) {
      return;
    }
    if (!message.outcoming && !message.deliveryStatus?.anyRead) {
      this.store.dispatch(
        changeMessageDeliveryStatus({
          channelUID: this.selectedChannel.uid,
          messageUID: message.uid,
          status: 'read',
        })
      );
      if ((this.selectedChannel.unreadMessages ?? 0) > 0) {
        if (this.selectedChannel.unreadMessages === 1) {
          this.store.dispatch(changeReadChannelStatus({ channel: this.selectedChannel, value: false }));
        }
        this.store.dispatch(
          setUnreadChannelCount({ value: (this.selectedChannel.unreadMessages ?? 0) - 1, channel: this.selectedChannel })
        );
      }
    }
  }

  clearUnreadMessages(message: IMessage): void {
    if (!this.selectedChannel) {
      return;
    }
    if (
      this.permissionsService.checkWorkspaceFeatures([
        WorkspaceFeatures.setChannelOwnerByUserId,
        WorkspaceFeatures.setChannelOwnerByTag,
      ])
    ) {
      if (
        this.profileInfo?.subjectMessageableEntity?.businessReference?.entityUID ===
        this.getTagValueByParam(this.selectedChannel, 'ownerId')
      ) {
        this.clearMessage(message);
      }
    } else {
      this.clearMessage(message);
    }
    if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.autoMarkAsRead])) {
      this.clearMessage(message);
    }
  }

  checkClearScrollMessage() {
    if (!this.selectedChannel) {
      return;
    }
    if (
      this.permissionsService.checkWorkspaceFeatures([
        WorkspaceFeatures.setChannelOwnerByUserId,
        WorkspaceFeatures.setChannelOwnerByTag,
      ])
    ) {
      if (
        this.profileInfo?.subjectMessageableEntity?.businessReference?.entityUID ===
        this.getTagValueByParam(this.selectedChannel, 'ownerId')
      ) {
        this.store.dispatch(changeReadChannelStatus({ channel: this.selectedChannel, value: false }));
      }
    } else {
      this.store.dispatch(changeReadChannelStatus({ channel: this.selectedChannel, value: false }));
    }
  }

  getOldMessages() {
    if (!this.selectedChannel) {
      return;
    }
    if (
      this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.canDoScrollMessages]) &&
      !this.selectedChannel.isLoadingOldMessages &&
      !this.selectedChannel.messagesCompleted
    ) {
      this.store.dispatch(setLoadingOldMessages({ channelUID: this.selectedChannel.uid, value: true }));
      this.store.dispatch(
        getMessages({
          channel: this.selectedChannel,
          concat: true,
          setLoading: false,
        })
      );
    }
  }

  setMessagingApiMessage(message: IMessage): IMessage {
    const newMessage: IMessage = {
      outcoming: message.outcoming,
      body: message.body,
      uid: message.uid,
      createdBy: message.createdBy,
      createdAt: new Date(message.createdAt),
      deliveryStatus: message.deliveryStatus,
      attachments: this.attachmentsService.fallbackAttachments(message.attachments ?? []),
      isDeleted: message.isDeleted,
      systemTags: message.systemTags,
      source: message.source,
      translation: message.translation,
      type: message.type,
      origin: message.origin,
      reactions: message.reactions,
    };
    if (message?.deliveryStatus?.anyFailed && message?.deliveryStatus.failed?.length) {
      newMessage.errorCode = this.getErrorCode(message);
      newMessage.errorMessage = this.getErrorMessage(newMessage);
    }
    return newMessage;
  }

  getErrorCode(message: IMessage): number {
    if (message?.deliveryStatus?.failed?.length) {
      return 9902;
    }
    return message?.deliveryStatus?.failed?.[0]?.errorCode === 9902 ||
      message?.deliveryStatus?.failed?.[0]?.errorCode === 902
      ? 9902
      : 400;
  }

  getErrorMessage(message: IMessage): string {
    return message.errorCode === 9902
      ? 'We encountered an issue while attempting to deliver your message.  Please try again later or contact customer support if the issue persists'
      : 'we could not retrieve the delivery status for this message';
  }

  createMessage(body: string, attachments: IAttachment[], tags: string[]): IMessage {
    const uid = new Date().toISOString() + '_' + this.selectedChannel?.uid;
    const message: IMessage = {
      outcoming: true,
      body: body,
      uid: uid,
      systemTags: [
        `messageUID:${uid}`,
        `ownerId:${this.profileInfo?.subjectMessageableEntity?.businessReference?.entityUID}`,
        `ownerDisplayName:${this.profileInfo?.subjectMessageableEntity?.displayName}`,
      ],
      createdBy: this.profileInfo!.subjectMessageableEntity,
      createdAt: new Date(),
      deliveryStatus: DeliveryStatusTypesOptions.sending,
      attachments: attachments,
    };
    const orderId = this.selectedChannel ? (this.getTagValueByParam(this.selectedChannel, 'orderId') as string) : '';
    const newOrderId = this.selectedChannel ? (this.getTagValueByParam(this.selectedChannel, 'newOrderId') as string) : '';
    if (newOrderId || orderId) {
      if (newOrderId) {
        message.systemTags?.push(`orderId:${newOrderId}`);
      } else if (orderId) {
        message.systemTags?.push(`orderId:${orderId}`);
      }
    }
    if (tags) {
      message.systemTags = [...(message.systemTags ?? []), ...tags];
    }
    return message;
  }

  // Messages ownership

  setMessagesOwnership(messages: IMessage[], users: IMessageableEntity[] = this.users): IMessage[] {
    if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.setMessageOwnerByUserId]) && users.length) {
      messages = this.setMessagesOwnersByUserID(messages, users);
    }
    if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.setMessageOwnerByTag])) {
      messages = this.setMessagesOwnersByTag(messages);
    }
    if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.setMessageOwnerByCreatedBy])) {
      messages = this.setMessagesOwnersByCreatedBy(messages);
    }
    return messages;
  }

  setMessagesOwnersByUserID(messages: IMessage[], users: IMessageableEntity[]): IMessage[] {
    messages = messages.map((message) => {
      if (message.outcoming) {
        const newMessage = { ...message };
        const ownerId = this.getTagValueByParam(message, 'ownerId');
        if (ownerId) {
          const user = findOwnerUser(users, ownerId);
          if (user) {
            newMessage.autor = {
              displayName: getTagValue(message.systemTags, 'ownerDisplayName') || user.displayName,
              profileImageURL: user.profileImageURL,
              id: user.businessReference?.entityUID || user.uid,
            };
          }
        } else {
          newMessage.autor = {
            displayName: 'Automatic message',
            id: '',
          };
        }
        if (message.origin) {
          if (message.origin.toUpperCase() === 'AI') {
            newMessage.autor = {
              displayName: 'AI BOT',
              id: '',
            };
          }
        }
        return newMessage;
      } else {
        return message;
      }
    });
    return messages;
  }

  setMessagesOwnersByTag(messages: IMessage[]): IMessage[] {
    messages = messages.map((message) => {
      if (message.outcoming) {
        const newMessage = { ...message };
        const ownerId = this.getTagValueByParam(message, 'ownerId');
        const ownerDisplayName = this.getTagValueByParam(message, 'ownerDisplayName');
        if (ownerId && ownerDisplayName) {
          const user = findOwnerUser(this.users, ownerId);
          newMessage.autor = {
            displayName: (ownerDisplayName as string) || user?.displayName || '',
            id: ownerId as string,
            profileImageURL: user?.profileImageURL,
          };
        }
        if (message.origin) {
          if (message.origin.toUpperCase() === 'AI') {
            newMessage.autor = {
              displayName: 'AI BOT',
              id: '',
            };
          }
        }
        return newMessage;
      } else {
        return message;
      }
    });
    return messages;
  }

  setMessagesOwnersByCreatedBy(messages: IMessage[]): IMessage[] {
    messages = messages.map((message) => {
      const newMessage = { ...message };
      newMessage.autor = {
        displayName: message.createdBy.displayName,
        profileImageURL: message.createdBy.profileImageURL,
        id: message.createdBy.uid,
      };
      return newMessage;
    });
    return messages;
  }

  getTagValueByParam(message: IMessage | IChannel, param: string): string | number | null {
    return getTagValue(message?.systemTags, param);
  }

  isWindowsBrowser(): boolean {
    const userAgent = window.navigator.userAgent;
    return userAgent.includes('Windows') && !userAgent.includes('Android') && !userAgent.includes('Linux');
  }
}
